import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Paper,
  Typography,
  Theme,
  Box,
  Grid,
  Avatar,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { t } from "../../features/i18n";
import { AVATAR_LIST } from "../../features/constants/avatars";
import MessageComponent from "../MessageComponent";
import { updateUser } from "../../apiRequest/user";
import { useSelector } from "react-redux";
import { RootState } from "../../features/redux/store";

const color = "#F88931";

const useStyles = makeStyles<Theme>((theme) => ({
  paper: {
    // borderTop: `8px solid ${color}`,
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: "40px",
    maxHeight: "80vh",
    overflow: "auto",
  },
  avatarGrid: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxHeight: "50vh",
    overflow: "auto",
  },
  avatarItem: {
    cursor: "pointer",
    transition: "transform 0.2s",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  selectedAvatar: {
    border: `3px solid ${color}`,
    transform: "scale(1.1)",
  },
}));

interface IUpdateAvatarModal {
  open: boolean;
  onClose: () => void;
  currentAvatar?: string | null;
  onAvatarUpdate: (avatarUrl: string) => Promise<void>;
}

const UpdateAvatarModal: React.FC<IUpdateAvatarModal> = ({
  open,
  onClose,
  currentAvatar,
  onAvatarUpdate,
}) => {
  const classes = useStyles();
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(
    currentAvatar || null
  );
  const [loading, setLoading] = useState(false);
  const [successAlertOpen, setSuccessAlertOpen] = useState(false);
  const [errorAlertOpen, setErrorAlertOpen] = useState(false);

  const userCode = useSelector((state: RootState) => state.user.userCode);
  const userName = useSelector((state: RootState) => state.user.userName);
  const userEmail = useSelector((state: RootState) => state.user.email);
  const userLanguage = useSelector((state: RootState) => state.user.lang);

  const handleAvatarSelect = (avatarUrl: string) => {
    setSelectedAvatar(avatarUrl);
  };

  const handleSubmit = async () => {
    console.log("Selected Avatar:", selectedAvatar);
    console.log("User Code:", userCode);
    if (!selectedAvatar || !userCode) return;

    try {
      setLoading(true);
      const userInfo = {
        code: userCode,
        name: userName || "",
        email: userEmail || "",
        avatar: selectedAvatar,
        lang: userLanguage,
      };
      await updateUser(userInfo);
      setTimeout(() => {
        setSuccessAlertOpen(true);
        setTimeout(() => {
          onAvatarUpdate(selectedAvatar);
          onClose();
        }, 2000);
      }, 2000);
    } catch (error) {
      console.error('Chyba při odesílání:', error);
      setErrorAlertOpen(true);
      setLoading(false);
      setTimeout(() => {
        setErrorAlertOpen(false);
        onClose();
      }, 2000);
    }
  };

  useEffect(() => {
    if (open) {
      setLoading(false);
      setSuccessAlertOpen(false);
      setErrorAlertOpen(false);
    }
  }, [open]);

  return (
    <Modal open={open} onClose={onClose}>
      <Paper className={classes.paper}>
        {successAlertOpen ? (
          <MessageComponent
            errorChange={errorAlertOpen}
            errorMessage={t("update_avatar_error")}
            successMessage={t("update_avatar_success")}
          />
        ) : (
          <Box>
            <Typography variant="h5" color={color} gutterBottom>
              {t("select_avatar")}
            </Typography>

            <Grid container spacing={2} className={classes.avatarGrid}>
              {AVATAR_LIST.map((avatar) => (
                <Grid item xs={3} key={avatar.id}>
                  <Avatar
                    src={avatar.url}
                    className={`${classes.avatarItem} ${selectedAvatar === avatar.url
                      ? classes.selectedAvatar
                      : ""
                      }`}
                    onClick={() => handleAvatarSelect(avatar.url)}
                    sx={{ width: 80, height: 80, margin: "0 auto" }}
                  />
                  <Typography align="center" variant="caption">
                    {avatar.name}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  color,
                  borderColor: color,
                }}
              >
                {t("cancel")}
              </Button>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!selectedAvatar || loading}
                sx={{
                  bgcolor: color,
                  "&:hover": {
                    bgcolor: color,
                  },
                }}
              >
                {loading ? t("updating") : t("confirm")}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Modal>
  );
};

export default UpdateAvatarModal;
